import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import {
  getJobCandidateInteraction,
  ICandidate,
  getCandidateById,
  IJob,
  load,
  candidateAggregatedDataWithJobId,
  loadCandidateByCandidateId,
} from 'wisestep-api';
import { ICandidateAggregatedData } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { getCachedJob } from '../../../utils/cacheService';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import {
  syncCandidateObjects,
  syncPostgreAndESCandidate,
} from '../../candidates/utils/CandidateHelper';
import conversationWidgetStore from '../store/conversationWidgetStore';

import ConversationWidget from './ConversationWidget';
import WidgetLoader from './WidgetLoader';

type RecruiterWidgetState = {
  candidate: ICandidate | null;
  aggregation: ICandidateAggregatedData | null;
};

const RecruiterWidget = () => {
  const [isCommonCandidateDataSet, setIsCommonCandidateDataSet] = useState(false);
  const id = useConversationWidgetStore((state) => state.id);
  const recievedCandidate = useConversationWidgetStore((state) => state.candidate);
  const cachedJob: IJob | null = getCachedJob();
  const view = useConversationWidgetStore((state) => state.view);
  const fromPage = useConversationWidgetStore((state) => state.fromPage);
  const isWidgetOpen = useConversationWidgetStore((state) => state.isWidgetOpen);
  const isNativeSearch = useConversationWidgetStore((state) => state.isNativeSearch);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);
  const setContactDetails = useConversationWidgetStore((state) => state.setContactDetails);
  const updateSelectedJob = conversationWidgetStore((state) => state.updateSelectedJob);
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const setPipelineCandidateId = useConversationWidgetStore(
    (state) => state.setPipelineCandidateId
  );
  const [isStateInitialized, setIsStateInitialized] = useState(false);
  const [recruiterWidgetState, setRecruiterWidgetState] = useState<RecruiterWidgetState>({
    candidate: null,
    aggregation: null,
  });
  const workSpace = useWorkspaceContext();

  useEffect(() => {
    if (isEmpty(selectedJob) && !isEmpty(cachedJob)) {
      updateSelectedJob(cachedJob);
    }
  }, [cachedJob, selectedJob, updateSelectedJob]);

  useEffect(() => {
    if (id == null && recievedCandidate == null) {
      setRecruiterWidgetState({
        candidate: null,
        aggregation: null,
      });
    }
    setIsStateInitialized(false);
  }, [selectedJob, id, recievedCandidate]);

  useEffect(() => {
    if (!isStateInitialized) {
      if (id || recievedCandidate) {
        if (fromPage !== 'talentpool' && recievedCandidate?.candidateId) {
          setPipelineCandidateId(recievedCandidate.candidateId);
        }
        (async () => {
          const job: IJob | null = getCachedJob();
          let candidateData: ICandidate | null = recievedCandidate;
          let aggregatedData: ICandidateAggregatedData | null = null;
          if (fromPage == 'talentpool' && id) {
            try {
              const req = await getCandidateById(id);
              const tempCandidateData = req.data;
              if (candidateData) {
                syncCandidateObjects(tempCandidateData, candidateData);
              }
              candidateData = tempCandidateData;
              if (candidateData?.email) {
                const linkedCandidateResponse = await load(
                  candidateData?.email ?? '',
                  workSpace.id ?? sessionStorage.getItem('workspaceId') ?? ''
                );
                if (candidateData && linkedCandidateResponse.data.dnc) {
                  candidateData.dncDetails = linkedCandidateResponse.data.dnc;
                  candidateData.isInDnc = linkedCandidateResponse.data.dnc.isInDnc;
                }
              }
            } catch (e) {
              console.error(e);
              setRecruiterWidgetState({
                candidate: null,
                aggregation: null,
              });
              setIsWidgetOpen(false);
            }
          } else if (id) {
            try {
              const req = await getJobCandidateInteraction(id);
              const tempCandidateData = req.data;
              if (candidateData) {
                syncCandidateObjects(tempCandidateData, candidateData);
              }
              candidateData = tempCandidateData;
              if (candidateData?.email) {
                const linkedCandidateResponse = await load(
                  candidateData?.email ?? '',
                  workSpace.id ?? sessionStorage.getItem('workspaceId') ?? ''
                );
                if (candidateData && linkedCandidateResponse.data.dnc) {
                  candidateData.dncDetails = linkedCandidateResponse.data.dnc;
                  candidateData.isInDnc = linkedCandidateResponse.data.dnc.isInDnc;
                }
              }
            } catch (e) {
              console.error(e);
              setRecruiterWidgetState({
                candidate: null,
                aggregation: null,
              });
              setIsWidgetOpen(false);
            }
          }
          setIsWidgetOpen(true);
          try {
            if (candidateData) {
              const req = await candidateAggregatedDataWithJobId(
                {
                  email: candidateData.email,
                  candidateProfileURLs: Array.isArray(candidateData?.profileURLs)
                    ? candidateData.profileURLs
                    : [],
                  linkedInURL: candidateData?.linkedInURL ?? '',
                  passportNumber: candidateData?.passportNumber ?? '',
                },
                view !== 'submission' ? job?.id : null
              );
              aggregatedData = req;
              setIsStateInitialized(true);
            }
            if (isNativeSearch) {
              setNotification(success('Candidate details successfully added to talentpool.'));
            }
          } catch (error) {
            setNotification(warning('Something went wrong, Please try again.'));
          }
          const tempCandidate = candidateData;
          if (
            !isCommonCandidateDataSet &&
            view === 'submission' &&
            aggregatedData &&
            aggregatedData.candidateEntity.candidateId &&
            tempCandidate
          ) {
            const commonCandidateData = await loadCandidateByCandidateId(
              aggregatedData.candidateEntity.candidateId
            );
            syncPostgreAndESCandidate(tempCandidate, commonCandidateData.data);
            setIsCommonCandidateDataSet(true);
          }
          candidateData && setContactDetails(candidateData);
          setRecruiterWidgetState({
            candidate: tempCandidate,
            aggregation: aggregatedData,
          });
        })();
      } else {
        setRecruiterWidgetState({
          candidate: null,
          aggregation: null,
        });
      }
    }
  }, [
    fromPage,
    id,
    isCommonCandidateDataSet,
    isNativeSearch,
    isStateInitialized,
    recievedCandidate,
    setContactDetails,
    setIsWidgetOpen,
    setNotification,
    setPipelineCandidateId,
    view,
    workSpace.id,
  ]);

  return (
    <>
      {(id || recievedCandidate) && isWidgetOpen && <WidgetLoader />}
      {(id || recievedCandidate) && recruiterWidgetState.candidate && (
        <ConversationWidget
          candidate={recruiterWidgetState.candidate}
          aggregation={recruiterWidgetState.aggregation}
          selectedJob={selectedJob}
        />
      )}
    </>
  );
};

export default RecruiterWidget;
